/** Data-free Connected apps UI. Hosts retain transport, authority and copy policy. */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { ConnectedAppRow, ConnectedAppSetup } from '@solum/experience'
import { Button, Card, CopyField, EmptyState, ErrorState, Notice, ServerCheck, SkeletonText, type ServerCheckStatus, type Tone } from '../controls'
import { readableError } from '@solum/format'
import { DataTable } from '../DataTable'
import { PageSection } from '../shell/PageSection'
import s from './ConnectedAppsSurface.module.css'

type TableRow = ConnectedAppRow & Record<string, unknown>
export type ConnectedAppsTestIds = { setupSection: string; setup: string; setupStatus?: string; list: string }
export type ConnectedAppsSetupState =
  | { kind: 'loading' }
  | { kind: 'failed'; retrying: boolean; onRetry: () => void }
  | { kind: 'ready'; value: ConnectedAppSetup }
export type ConnectedAppsListState =
  | { kind: 'loading' }
  | { kind: 'failed'; onRetry: () => void }
  | { kind: 'ready'; rows: readonly ConnectedAppRow[]; contextFailed: boolean; onRetryContext: () => void }
export type ConnectedAppsDisconnectResult = { ok: true } | { ok: false; message: string }
export type ConnectedAppsSurfaceProps = {
  resource: string | null
  setup: ConnectedAppsSetupState | null
  list: ConnectedAppsListState
  setupSummary: ReactNode
  notConfiguredDetail: string
  notEligibleDetail: string
  headingLevel?: 2 | 3
  message?: ReactNode
  testIds: ConnectedAppsTestIds
  onDisconnect: (clientId: string) => Promise<ConnectedAppsDisconnectResult>
  onDisconnectIntent?: () => void
  onDisconnected: (row: ConnectedAppRow) => void
  focusAfterDisconnect: () => void
}
type SetupView = { status: ServerCheckStatus; tone?: Tone; statusLabel: string; detail: string }

function setupView(value: ConnectedAppSetup, notConfigured: string, notEligible: string): SetupView {
  if (value === 'available') return { status: 'ok', statusLabel: 'Available', detail: 'Connect Claude or ChatGPT with just the address below.' }
  if (value === 'not-published') return { status: 'failed', tone: 'warn', statusLabel: 'Not set up yet', detail: 'Your administrator hasn’t finished setting up URL-only connections.' }
  if (value === 'not-configured') return { status: 'failed', tone: 'neutral', statusLabel: 'Not configured', detail: notConfigured }
  return { status: 'failed', tone: 'warn', statusLabel: 'Not available to you', detail: notEligible }
}

function ids(stems: ConnectedAppsTestIds) {
  const status = stems.setupStatus ?? stems.setup
  return {
    setupSection: stems.setupSection, status: `${status}-status`, retry: `${status}-retry`,
    address: `${stems.setup}-address`, steps: `${stems.setup}-steps`, claudeCode: `${stems.setup}-claude-code`,
    listSection: `${stems.list}-list`, loading: `${stems.list}-loading`, message: `${stems.list}-message`,
    confirm: `${stems.list}-confirm`, confirmDisconnect: `${stems.list}-confirm-disconnect`,
    confirmCancel: `${stems.list}-confirm-cancel`, failure: `${stems.list}-failure`,
    contextFailure: `${stems.list}-context-failed`, table: `${stems.list}-table`,
    disconnect: (clientId: string) => `${stems.list}-disconnect-${clientId}`,
  }
}

export function ConnectedAppsSurface({
  resource, setup, list, setupSummary, notConfiguredDetail, notEligibleDetail, headingLevel = 2,
  message, testIds, onDisconnect, onDisconnectIntent, onDisconnected, focusAfterDisconnect,
}: ConnectedAppsSurfaceProps) {
  const [confirming, setConfirming] = useState<ConnectedAppRow | null>(null)
  const [revoking, setRevoking] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const confirmRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLElement | null>(null)
  const test = ids(testIds)
  useEffect(() => { if (confirming) confirmRef.current?.focus() }, [confirming])

  async function revoke(row: ConnectedAppRow) {
    setRevoking(true); setFailure(null)
    try {
      const result = await onDisconnect(row.clientId)
      if (!result.ok) { setFailure(readableError(result.message, 'Could not disconnect that app.')); return }
      setConfirming(null); onDisconnected(row); focusAfterDisconnect()
    } catch { setFailure('Could not disconnect that app.') }
    finally { setRevoking(false) }
  }
  const presented = setup?.kind === 'ready' ? setupView(setup.value, notConfiguredDetail, notEligibleDetail) : null

  return (<>
    {resource !== null && setup !== null && <PageSection headingLevel={headingLevel} title="Connect Claude or ChatGPT"
      testId={test.setupSection} summary={setupSummary}><Card pad className={s.setup}>
      {setup.kind === 'loading' ? <ServerCheck label="URL-only setup" status="checking" data-testid={test.status} />
        : setup.kind === 'failed' || !presented ? <ServerCheck label="URL-only setup" status="unavailable"
          statusLabel="Couldn’t check" detail="Marble didn’t answer, so this isn’t a no. Try again."
          data-testid={test.status} action={<Button size="sm" variant="secondary"
            disabled={setup.kind === 'failed' && setup.retrying} data-testid={test.retry}
            onClick={setup.kind === 'failed' ? setup.onRetry : undefined}>Retry</Button>} />
          : <ServerCheck label="URL-only setup" status={presented.status} tone={presented.tone}
            statusLabel={presented.statusLabel} detail={presented.detail} data-testid={test.status} />}
      {setup.kind === 'ready' && setup.value === 'available' && <>
        <CopyField label="Connector address" value={resource} testId={test.address} />
        <ol className={s.steps} data-testid={test.steps}>
          <li>Copy the connector address.</li>
          <li>In Claude, add a custom connector (Settings → Connectors). In ChatGPT, add it as a connector (app).</li>
          <li>Paste the address and choose “Sign in now”.</li>
          <li>In Claude, choose “Use Claude’s published identity”. ChatGPT uses its published identity automatically.</li>
          <li>Review the Marble consent screen and choose Allow.</li>
        </ol>
      </>}
      <p className="supporting" data-testid={test.claudeCode}>Claude Code can’t connect this way yet. It keeps the setup your administrator provides.</p>
    </Card></PageSection>}

    <PageSection headingLevel={headingLevel} title="Apps with access" testId={test.listSection}>
      {message != null && <Notice tone="info" live="status" data-testid={test.message}>{message}</Notice>}
      {list.kind === 'loading' ? <Card pad role="status" aria-label="Loading connected apps" data-testid={test.loading}><SkeletonText lines={3} /></Card>
        : list.kind === 'failed' ? <Card pad><ErrorState onRetry={list.onRetry} /></Card>
          : list.rows.length === 0 ? <Card pad><EmptyState>No connected apps. Apps you allow will appear here, and you can disconnect them at any time.</EmptyState></Card>
            : <>
              {confirming && <Notice tone="warn" live="alert" ref={confirmRef} tabIndex={-1} data-testid={test.confirm}
                actions={<><Button variant="danger" disabled={revoking} onClick={() => void revoke(confirming)} data-testid={test.confirmDisconnect}>
                  {revoking ? 'Disconnecting…' : 'Disconnect'}</Button><Button variant="secondary" disabled={revoking}
                    data-testid={test.confirmCancel} onClick={() => { setConfirming(null); setFailure(null); triggerRef.current?.focus() }}>Cancel</Button></>}>
                Disconnect {confirming.name}? It stops working immediately, including anything it is doing now.
              </Notice>}
              {failure && <Notice tone="critical" live="alert" data-testid={test.failure}>{failure}</Notice>}
              {list.contextFailed && <Notice tone="warn" data-testid={test.contextFailure}
                actions={<Button variant="secondary" size="sm" onClick={list.onRetryContext}>Retry</Button>}>
                Couldn’t check which part of Marble each app reaches. Every app is still listed, and you can disconnect any of them.
              </Notice>}
              <Card pad><DataTable<TableRow> caption="Apps connected to your account" testId={test.table}
                rows={[...list.rows] as TableRow[]} rowKey={(row) => row.clientId} columns={[
                  { key: 'name', label: 'App' }, { key: 'surface', label: 'Connected to' }, { key: 'since', label: 'Connected' },
                  { key: 'actions', label: 'Actions', cell: (row) => <Button variant="ghost" size="sm" disabled={revoking}
                    aria-label={`Disconnect ${row.name}`} data-testid={test.disconnect(row.clientId)} onClick={(event) => {
                      triggerRef.current = event.currentTarget; setFailure(null); onDisconnectIntent?.(); setConfirming(row)
                    }}>Disconnect</Button> },
                ]} /></Card>
            </>}
    </PageSection>
  </>)
}
